import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Plug, RefreshCw, Unplug, Send, Inbox, Loader2, Search, UserRound, FileText, MessagesSquare } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { PageHeader, EmptyState, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TemplatesManager } from "@/components/TemplatesManager";
import { SignatureEditor } from "@/components/SignatureEditor";
import { Avatar } from "@/components/CandidateCard";
import CandidatePanel from "@/components/CandidatePanel";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { t as tr, tk } from "@/i18n";
import { fmtDateTime } from "@/i18n/format";

const CONV_TONE = { awaiting_reply: "amber", replied: "green" };
const CONV_LABEL = { awaiting_reply: tk("Awaiting reply"), replied: tk("Replied") };
const FILTERS = [["all", tk("All")], ["awaiting_reply", tk("Awaiting reply")], ["replied", tk("Replied")]];

function MailboxForm({ onDone }) {
  const [f, setF] = useState({ email: "", password: "" });
  const [d, setD] = useState(null);
  const [adv, setAdv] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const detect = async () => {
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return;
    const { data } = await api.post("/mail/imap/detect", { email: f.email });
    setD(data); setF((x) => ({ ...x, imap_host: data.imap_host, imap_port: data.imap_port, smtp_host: data.smtp_host, smtp_port: data.smtp_port, smtp_security: data.smtp_security }));
    if (!data.detected) setAdv(true);
  };
  const connect = async () => {
    setBusy(true);
    try { await api.post("/mail/imap/connect", { ...f, imap_port: Number(f.imap_port) || null, smtp_port: Number(f.smtp_port) || null }); toast.success(tr("Mailbox connected")); setF({ email: "", password: "" }); setD(null); onDone(); }
    catch (e) { toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  const F = (k, label, type = "text") => <label className="text-xs space-y-1"><span className="text-muted-foreground">{label}</span><Input type={type} value={f[k] ?? ""} onChange={set(k)} className="h-9" data-testid={`mailbox-${k}`} /></label>;
  return (
    <div className="card-surface p-5 mb-6 space-y-3" data-testid="mailbox-form">
      <div className="font-semibold text-sm flex items-center gap-2"><Plug className="w-4 h-4 text-primary" />{tr("Connect a mailbox")}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs space-y-1"><span className="text-muted-foreground">{tr("Email address")}</span><Input type="email" value={f.email} onChange={set("email")} onBlur={detect} placeholder={tr("you@company.com")} className="h-9" data-testid="mailbox-email" /></label>
        {F("password", "App password", "password")}
      </div>
      {d && <div className="text-xs flex flex-wrap items-center gap-2" data-testid="mailbox-detected"><Pill tone={d.detected ? "green" : "amber"}>{d.label}</Pill>
        {d.help_url && <a href={d.help_url} target="_blank" rel="noreferrer" className="text-primary underline">{tr("How to get an app password")}</a>}</div>}
      <div><button type="button" onClick={() => setAdv(!adv)} className="text-xs text-muted-foreground underline" data-testid="mailbox-advanced-toggle">{tr("Advanced settings")}</button></div>
      {adv && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="mailbox-advanced">{F("imap_host", "IMAP server")}{F("imap_port", "Port", "number")}{F("smtp_host", "SMTP server")}{F("smtp_port", "Port", "number")}
        <div className="col-span-2">{F("username", "Username (if different)")}</div>
        <label className="text-xs space-y-1 col-span-2"><span className="text-muted-foreground">SMTP</span><select value={f.smtp_security || "ssl"} onChange={set("smtp_security")} className="h-9 w-full rounded-md border bg-background px-2 text-sm" data-testid="mailbox-smtp_security"><option value="ssl">SSL/TLS</option><option value="starttls">STARTTLS</option></select></label></div>}{/* i18n-ignore */}
      <Button size="sm" disabled={busy || !f.email || !f.password} onClick={connect} data-testid="mailbox-connect">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}{busy ? tr("Connecting…") : tr("Test & connect")}</Button>
    </div>
  );
}

function Mailboxes({ status, reload }) {
  const disconnect = async (a) => { await api.delete(`/mail/accounts/${a.id}`); toast.success(tr("Mailbox disconnected")); reload(); };
  return (
    <>
      {status.accounts.map((a) => (
        <div key={a.id} className="card-surface p-4 mb-3 flex items-center gap-3" data-testid={`mailbox-account-${a.id}`}>
          <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center"><Mail className="w-4 h-4 text-accent-foreground" /></div>
          <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate" translate="no">{a.address}</div><div className="text-xs text-muted-foreground">{a.label}</div></div>
          <Pill tone={a.status === "connected" ? "green" : "amber"}>{a.status === "connected" ? tr("Connected") : tr("Reconnection needed")}</Pill>
          <Button size="sm" variant="outline" onClick={() => disconnect(a)} data-testid={`disconnect-${a.id}`}><Unplug className="w-4 h-4" />{tr("Disconnect")}</Button>
        </div>
      ))}
      {!status.accounts.some((a) => a.status === "connected") && <MailboxForm onDone={reload} />}
    </>
  );
}

function Thread({ id, onChange, onProfile }) {
  const [c, setC] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setC(null); api.get(`/mail/conversations/${id}`).then((r) => setC(r.data)); }, [id]);
  const reply = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/mail/conversations/${id}/reply`, { body }); setC(data); setBody(""); onChange(); } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(false); }
  };
  if (!c) return <div className="h-40 rounded-xl bg-muted shimmer" />;
  return (
    <div className="card-surface flex flex-col h-full min-h-[480px]" data-testid="conversation-thread">
      <div className="flex items-center gap-3 p-4 border-b">
        <Avatar name={c.candidate_name} />
        <div className="flex-1 min-w-0"><div className="font-semibold truncate" data-testid="thread-subject">{c.subject}</div>
          <div className="text-xs text-muted-foreground truncate">{c.candidate_name} · <span translate="no">{c.to}</span> · {tr("via {{provider}}", { provider: c.provider })} · {c.owner_name}</div></div>
        <Pill tone={CONV_TONE[c.status]}>{tr(CONV_LABEL[c.status] || c.status)}</Pill>
        <Button size="sm" variant="outline" onClick={() => onProfile(c.candidate_id)} data-testid="thread-open-profile"><UserRound className="w-4 h-4" />{tr("Profile")}</Button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20">
        {c.messages.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`} className={`rounded-2xl px-4 py-2.5 text-sm max-w-[85%] shadow-sm ${m.direction === "outbound" ? "ml-auto bg-primary text-primary-foreground rounded-br-sm" : "bg-card border rounded-bl-sm"}`}>
            <div className="whitespace-pre-wrap">{m.body}</div>
            <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.direction === "outbound" ? tr("You") : m.sender} · {fmtDateTime(m.sent_at)}</div>
          </div>
        ))}
      </div>
      {c.is_mine ? <div className="p-3 border-t flex gap-2 items-end"><Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={tr("Reply…")} className="flex-1" data-testid="reply-input" />
        <Button disabled={busy || !body.trim()} onClick={reply} data-testid="reply-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{tr("Reply")}</Button></div>
        : <p className="p-3 border-t text-xs text-muted-foreground">{tr("Only the recruiter who started this conversation can reply from their mailbox.")}</p>}
    </div>
  );
}

function ConvList({ convs, sel, setSel }) {
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const rows = convs.filter((c) => (f === "all" || c.status === f) && `${c.candidate_name} ${c.subject}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="card-surface flex flex-col min-h-[480px]" data-testid="conversation-list">
      <div className="p-3 border-b space-y-2">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Search a candidate or subject…")} className="h-9 pl-8" data-testid="conversation-search" /></div>
        <div className="flex gap-1">{FILTERS.map(([k, l]) => <button key={k} onClick={() => setF(k)} data-testid={`conversation-filter-${k}`} className={`px-2.5 h-7 rounded-full text-[11px] font-medium transition-colors ${f === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{tr(l)} ({k === "all" ? convs.length : convs.filter((c) => c.status === k).length})</button>)}</div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rows.map((c) => (
          <button key={c.id} onClick={() => setSel(c.id)} data-testid={`conversation-${c.id}`} className={`w-full text-left flex gap-3 px-3 py-3 border-b transition-colors ${sel === c.id ? "bg-accent" : "hover:bg-muted/50"}`}>
            <Avatar name={c.candidate_name} size={9} />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2"><span className="text-sm font-semibold truncate">{c.candidate_name}</span><span className="text-[10px] text-muted-foreground shrink-0">{c.last_message_at && fmtDateTime(c.last_message_at)}</span></div>
              <div className="text-xs text-muted-foreground truncate">{c.subject}</div>
              <div className="flex items-center gap-2 mt-1"><Pill tone={CONV_TONE[c.status]}>{tr(CONV_LABEL[c.status] || c.status)}</Pill><span className="text-[10px] text-muted-foreground truncate">{tr("{{count}} messages", { count: c.message_count })} · {c.owner_name}</span></div>
            </div>
          </button>
        ))}
        {!rows.length && <p className="p-4 text-sm text-muted-foreground" data-testid="conversation-no-match">{tr("No conversation matches.")}</p>}
      </div>
    </div>
  );
}

export default function Messages() {
  const [status, setStatus] = useState(null);
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [panel, setPanel] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = () => { api.get("/mail/status").then((r) => setStatus(r.data)); api.get("/mail/conversations").then((r) => setConvs(r.data)); };
  useEffect(() => {
    load();
    if (params.get("connected")) toast.success(tr("Mailbox connected"));
    if (params.get("mail_error")) toast.error(tr("Mailbox connection failed. Please try again."));
    if (params.toString()) setParams({}, { replace: true });
  }, []); // eslint-disable-line
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/mail/sync"); toast.success(tr("{{count}} new replies", { count: data.new_replies })); load(); } catch (e) { toast.error(errorDetail(e)); } finally { setSyncing(false); }
  };
  if (!status) return <div className="p-6"><div className="h-40 rounded-xl bg-muted shimmer" /></div>;
  const connected = status.accounts.some((a) => a.status === "connected");
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={tr("Outreach")} title={tr("Messages")} desc={tr("Send from your own mailbox and track every candidate conversation. Your password is encrypted and never leaves the server.")}
        actions={<Button size="sm" variant="outline" onClick={sync} disabled={syncing || !status.accounts.length} data-testid="sync-mail-button"><RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />{tr("Sync replies")}</Button>} />
      <Tabs defaultValue={connected ? "conversations" : "mailbox"}>
        <TabsList className="mb-4" data-testid="messages-tabs">
          <TabsTrigger value="conversations" data-testid="tab-conversations"><MessagesSquare className="w-4 h-4 mr-1.5" />{tr("Conversations")} ({convs.length})</TabsTrigger>
          <TabsTrigger value="mailbox" data-testid="tab-mailbox"><Mail className="w-4 h-4 mr-1.5" />{tr("Mailbox")}{!connected && <span className="ml-1.5 w-2 h-2 rounded-full bg-amber-500" />}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><FileText className="w-4 h-4 mr-1.5" />{tr("Templates")}</TabsTrigger>
        </TabsList>
        <TabsContent value="conversations">
          {!convs.length ? <EmptyState icon={Inbox} title={tr("No conversations yet")} body={tr("Open a candidate profile and click “Contact candidate” to start a conversation.")} testid="conversations-empty" /> : (
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2"><ConvList convs={convs} sel={sel} setSel={setSel} /></div>
              <div className="lg:col-span-3">{sel ? <Thread id={sel} onChange={load} onProfile={setPanel} /> : <EmptyState icon={Mail} title={tr("Select a conversation")} />}</div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="mailbox"><Mailboxes status={status} reload={load} /><SignatureEditor /></TabsContent>
        <TabsContent value="templates"><TemplatesManager /></TabsContent>
      </Tabs>
      {panel && <aside className="fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] bg-background border-l shadow-xl overflow-y-auto" data-testid="messages-profile-panel"><CandidatePanel id={panel} onClose={() => setPanel(null)} /></aside>}
    </div>
  );
}
